"use client";

import React, { useState } from "react";
import { ArrowLeft, Eye, EyeOff, Mail, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Cookies from "js-cookie";
import useApi from "@/hooks/useApi";
import { toast } from "sonner";
import API_ENDPOINTS from "@/constants/endpoints";
import { useUser } from "@/context/userContext";

export default function Login() {
  const router = useRouter();
  const { fetchData } = useApi();
  const { setUser } = useUser();

  // Login state
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  // Forgot password state
  const [forgotStep, setForgotStep] = useState<
    "login" | "email" | "otp" | "reset"
  >("login");
  const [forgotData, setForgotData] = useState({
    email: "",
    otp: "",
    newPassword: "",
  });

  const initialForgotData = {
  email: "",
  otp: "",
  newPassword: "",
};

const resetForgotFlow = () => {
  setForgotData(initialForgotData);
  setErrors({});
  setShowPassword(false);
};

const goToLogin = () => {
  resetForgotFlow();
  setForgotStep("login");
};

  // Handle input changes
  const handleInputChange = (field: string, value: string | boolean) => {
    if (forgotStep === "login") {
      setFormData((prev) => ({ ...prev, [field]: value }));
    } else {
      setForgotData((prev) => ({ ...prev, [field]: value }));
    }
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Login validation
  const validateLoginForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.username) newErrors.username = "Username is required";
    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateLoginForm()) return;

    setIsLoading(true);

    const res = await fetchData(
      API_ENDPOINTS.LOGIN,
      "POST",
      {
        username: formData.username.trim(),
        password: formData.password.trim(),
      },
      {},
      false,
      false
    );

    if (res && res.access_token) {
      Cookies.set("authToken", res.access_token);
      Cookies.set("plagpro_user", JSON.stringify(res.user));
      toast.success("Login successful!");
      setUser(res.user);
      const role = res.user.role_name.toLowerCase();

      const redirectRole = role === "admin" ? role : "user";

      router.push(`/${redirectRole}`);
    } else {
      toast.error("Invalid credentials, please try again.");
    }

    setIsLoading(false);
  };

  // Forgot password steps
  const handleForgotNext = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      if (forgotStep === "email") {
        const email = forgotData.email.trim();
        if (!email) {
          setErrors({ email: "Email is required" });
          setIsLoading(false);
          return;
        }

        const res = await fetchData(
          API_ENDPOINTS.FORGOT_PASSWORD_SEND,
          "POST",
          { email },
          {},
          false,
          true
        );

        if (res !== null) {
          setForgotStep("otp");
        }
      } else if (forgotStep === "otp") {
        if (!forgotData.otp.trim()) {
          setErrors({ otp: "OTP is required" });
          setIsLoading(false);
          return;
        }

        const res = await fetchData(
          API_ENDPOINTS.FORGOT_PASSWORD_VERIFY,
          "POST",
          {
            email: forgotData.email.trim(),
            otp: forgotData.otp.trim(),
          },
          {},
          false,
          true
        );

        if (res !== null) {
          setForgotStep("reset");
        }
      } else if (forgotStep === "reset") {
        if (
          !forgotData.newPassword.trim() ||
          forgotData.newPassword.length < 8
        ) {
          setErrors({
            newPassword: "Password must be at least 8 characters",
          });
          setIsLoading(false);
          return;
        }

        const res = await fetchData(
          API_ENDPOINTS.FORGOT_PASSWORD_RESET,
          "POST",
          {
            email: forgotData.email.trim(),
            otp: forgotData.otp.trim(),
            new_password: forgotData.newPassword.trim(),
          },
          {},
          false,
          true
        );

        if (res !== null) {
          toast.success("Password reset successfully. Please sign in.");
          goToLogin();
        }
      }
    } catch (err) {
      // console.error(err);
      toast.error("Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link href="/">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">P</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">PlagPro</span>
            </div>
            <CardTitle className="text-2xl">
              {forgotStep === "login"
                ? "Welcome Back"
                : forgotStep === "email"
                  ? "Forgot Password"
                  : forgotStep === "otp"
                    ? "Enter OTP"
                    : "Reset Password"}
            </CardTitle>
            <p className="text-gray-600 mt-2">
              {forgotStep === "login" && "Sign in to your account"}
              {forgotStep === "email" &&
                "Enter your account email to receive a verification code"}
              {forgotStep === "otp" &&
                "Enter the 6-digit verification code sent to your email"}
              {forgotStep === "reset" && "Set a new password"}
            </p>
          </CardHeader>

          <CardContent>
            {forgotStep === "login" && (
              <form onSubmit={handleLogin} className="space-y-6">
                {errors.general && (
                  <p className="text-sm text-red-600 text-center">
                    {errors.general}
                  </p>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type="text"
                      value={formData.username}
                      onChange={(e) =>
                        handleInputChange("username", e.target.value)
                      }
                      placeholder="Enter your username"
                      className={`pl-10 ${errors.username ? "border-red-500" : ""
                        } border-1 border-gray-200`}
                    />
                  </div>
                  {errors.username && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.username}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                      placeholder="Enter your password"
                      autoComplete="new-password"
                      className={`pl-10 pr-10 ${errors.password ? "border-red-500" : ""
                        } border-1 border-gray-200`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.password}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember"
                      checked={formData.rememberMe}
                      onCheckedChange={(checked) =>
                        handleInputChange("rememberMe", checked as boolean)
                      }
                    />
                    <label htmlFor="remember" className="text-sm text-gray-700">
                      Remember me
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      resetForgotFlow();
                      setForgotStep("email");
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Forgot password?
                  </button>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={isLoading}
                >
                  {isLoading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            )}

            {(forgotStep === "email" ||
              forgotStep === "otp" ||
              forgotStep === "reset") && (
                <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
                  {forgotStep === "email" && (
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={forgotData.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      className="border-1 border-gray-200"
                    />
                  )}
                  {forgotStep === "otp" && (
                    <Input
                      placeholder="Enter OTP"
                      value={forgotData.otp}
                      onChange={(e) => handleInputChange("otp", e.target.value)}
                      className="border-1 border-gray-200"
                    />
                  )}
                  {forgotStep === "reset" && (
                    // <Input
                    //   placeholder="New password"
                    //   type="password"
                    //   value={forgotData.newPassword}
                    //   onChange={(e) =>
                    //     handleInputChange("newPassword", e.target.value)
                    //   }
                    //   className="border-1 border-gray-200"
                    // />
                    <div className="relative">
                      <Input
                        placeholder="Password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        value={forgotData.newPassword}
                        onChange={(e) =>
                          handleInputChange("newPassword", e.target.value)
                        }
                        className="border border-gray-200 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  )}

                  {Object.values(errors).map((err, idx) => (
                    <p key={idx} className="text-sm text-red-600">
                      {err}
                    </p>
                  ))}

                  <Button
                    type="button"
                    className="w-full"
                    onClick={handleForgotNext}
                  >
                    {forgotStep === "email"
                      ? "Send Code"
                      : forgotStep === "otp"
                        ? "Verify OTP"
                        : "Reset Password"}
                  </Button>

                  <p
                    className="text-sm text-gray-600 mt-4 text-center cursor-pointer"
                    onClick={goToLogin}
                  >
                    Back to Login
                  </p>
                </form>
              )}

            <div className="mt-6">
              <Separator className="mb-6" />
              <p className="text-center text-sm text-gray-600">
                {`Don't have an account? `}
                <Link
                  href="/register"
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Sign up here
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
